import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { CheckCircle, AlertCircle, AlertTriangle, Info, RefreshCw, ClipboardCheck } from 'lucide-react';

// DesignGUIDE-style pre-run case-readiness check. Point it at a prepared case
// dir (before it's submitted externally) and it runs a license-free scorecard of
// the setup problems that commonly waste compute. Quick-picks come from
// /mnt/data0/shared/jobs-to-submit.txt.

const SEV = {
  fail: { color: '#f87171', bg: 'rgba(248,113,113,0.08)', border: 'rgba(248,113,113,0.3)', Icon: AlertCircle, label: 'Fail' },
  warn: { color: '#facc15', bg: 'rgba(250,204,21,0.08)', border: 'rgba(250,204,21,0.3)', Icon: AlertTriangle, label: 'Warn' },
  pass: { color: '#34d399', bg: 'rgba(52,211,153,0.06)', border: 'rgba(52,211,153,0.25)', Icon: CheckCircle, label: 'Pass' },
  info: { color: '#60a5fa', bg: 'rgba(96,165,250,0.07)', border: 'rgba(96,165,250,0.3)', Icon: Info, label: 'Info' },
};

function OverallBadge({ overall, summary }) {
  const s = SEV[overall] || SEV.info;
  const text = overall === 'pass' ? 'Ready to submit'
    : overall === 'warn' ? 'Ready with warnings'
    : 'Not ready';
  return (
    <div style={{
      display: 'inline-flex', alignItems: 'center', gap: 10, padding: '10px 16px',
      borderRadius: 10, backgroundColor: s.bg, border: `1px solid ${s.border}`,
    }}>
      <s.Icon style={{ width: 20, height: 20, color: s.color }} />
      <span style={{ color: s.color, fontWeight: 700, fontSize: 15 }}>{text}</span>
      <span style={{ color: '#6b7280', fontSize: 12 }}>
        {summary.pass} pass · {summary.warn} warn · {summary.fail} fail
      </span>
    </div>
  );
}

export default function CaseCheck() {
  const [path, setPath] = useState('');
  const [candidates, setCandidates] = useState([]);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    axios.get('/api/case-check/candidates')
      .then(r => { if (r.data?.success) setCandidates(r.data.candidates || []); })
      .catch(() => {});
  }, []);

  const runCheck = useCallback(async (p) => {
    const target = (p ?? path).trim();
    if (!target) return;
    setLoading(true); setError(null); setResult(null);
    try {
      const r = await axios.get('/api/case-check', { params: { path: target } });
      if (r.data?.success) setResult(r.data.result);
      else setError(r.data?.error || 'Check failed');
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setLoading(false);
    }
  }, [path]);

  return (
    <div style={{ maxWidth: 960 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
        <ClipboardCheck style={{ width: 20, height: 20, color: '#3b82f6' }} />
        <h2 style={{ color: '#fff', fontSize: 18, fontWeight: 700, margin: 0 }}>Case Readiness Check</h2>
      </div>
      <p style={{ color: '#9ca3af', fontSize: 13, marginTop: 0, marginBottom: 18, lineHeight: 1.5 }}>
        Validate a prepared case directory before it's submitted — catches missing case definitions,
        un-discretized meshes, bad node references, stale results and license-server issues. Config checks
        only (no PowerFLOW license used); deep CFD best-practices still need the Exa tools.
      </p>

      {/* Path input */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
        <input
          type="text"
          value={path}
          onChange={(e) => setPath(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') runCheck(); }}
          placeholder="/mnt/data0/shared/…/M0p90_Alpha2p00"
          style={{
            flex: 1, padding: '10px 12px', borderRadius: 8, border: '1px solid #1e293b',
            backgroundColor: '#0b1322', color: '#e5e7eb', fontSize: 13, fontFamily: 'monospace',
          }}
        />
        <button
          onClick={() => runCheck()}
          disabled={loading || !path.trim()}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 8, padding: '10px 18px',
            borderRadius: 8, border: 'none', fontSize: 13, fontWeight: 600,
            cursor: loading || !path.trim() ? 'default' : 'pointer',
            backgroundColor: loading || !path.trim() ? '#1e293b' : '#2563eb',
            color: loading || !path.trim() ? '#64748b' : '#fff',
          }}>
          {loading
            ? <><RefreshCw style={{ width: 14, height: 14, animation: 'spin 1s linear infinite' }} /> Checking…</>
            : 'Run check'}
        </button>
      </div>

      {/* Quick picks from jobs-to-submit.txt */}
      {candidates.length > 0 && (
        <div style={{ marginBottom: 20 }}>
          <div style={{ color: '#6b7280', fontSize: 11, fontWeight: 700, letterSpacing: 0.6, textTransform: 'uppercase', marginBottom: 8 }}>
            Queued to submit ({candidates.length})
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {candidates.map((c) => (
              <button
                key={c.path}
                onClick={() => { setPath(c.path); runCheck(c.path); }}
                title={c.path}
                style={{
                  padding: '5px 10px', borderRadius: 6, border: '1px solid #1e293b',
                  backgroundColor: '#0b1322', color: '#93c5fd', fontSize: 11,
                  fontFamily: 'monospace', cursor: 'pointer',
                }}>
                {c.name}
              </button>
            ))}
          </div>
        </div>
      )}

      {error && (
        <div style={{ padding: '12px 14px', borderRadius: 8, backgroundColor: SEV.fail.bg, border: `1px solid ${SEV.fail.border}`, color: SEV.fail.color, fontSize: 13 }}>
          {error}
        </div>
      )}

      {/* Scorecard */}
      {result && (
        <div style={{ backgroundColor: '#0f172a', borderRadius: 12, border: '1px solid #1e293b', padding: 20 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', marginBottom: 16 }}>
            <div>
              <div style={{ color: '#fff', fontSize: 15, fontWeight: 700 }}>{result.name}</div>
              <div style={{ color: '#6b7280', fontSize: 11, fontFamily: 'monospace', marginTop: 2 }}>{result.path}</div>
              {result.conditions?.aoa != null && (
                <div style={{ color: '#9ca3af', fontSize: 12, marginTop: 4 }}>
                  {result.conditions.mach != null && `Mach ${result.conditions.mach} · `}α {result.conditions.aoa}°
                </div>
              )}
            </div>
            <OverallBadge overall={result.overall} summary={result.summary} />
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {result.checks.map((c) => {
              const s = SEV[c.severity] || SEV.info;
              return (
                <div key={c.id} style={{
                  display: 'flex', alignItems: 'flex-start', gap: 12, padding: '10px 12px',
                  borderRadius: 8, backgroundColor: s.bg, border: `1px solid ${s.border}`,
                }}>
                  <s.Icon style={{ width: 16, height: 16, color: s.color, flexShrink: 0, marginTop: 1 }} />
                  <div style={{ flex: 1 }}>
                    <div style={{ color: '#e5e7eb', fontSize: 13, fontWeight: 600 }}>{c.label}</div>
                    <div style={{ color: '#9ca3af', fontSize: 12, marginTop: 2 }}>{c.detail}</div>
                  </div>
                  <span style={{ color: s.color, fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 }}>{s.label}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
